import { Injectable } from '@angular/core';
import { capSQLiteChanges, capSQLiteValues } from '@capacitor-community/sqlite';
import { Logger } from '../../log/logger';
import { DatabaseService } from '../database.service';
import { IDbSet } from '../dbset.interface';
import { ListingPhoto } from '../entities/listing-photo';

const TABLE_NAME = 'listing_photos';

@Injectable({
  providedIn: 'root',
})
export class ListingPhotosDbSet implements IDbSet {
  constructor(private databaseService: DatabaseService, private logger: Logger) {}

  public store(request: ListingPhoto): Promise<capSQLiteValues> {
    return this.databaseService.insert(TABLE_NAME, this.mapObjectsToStorable([request]).pop());
  }

  public storeArray(requests: ListingPhoto[]): Promise<capSQLiteValues> {
    return this.databaseService.insertMulti(TABLE_NAME, this.mapObjectsToStorable(requests));
  }

  public async truncateAndStoreArray(records: ListingPhoto[]): Promise<capSQLiteValues> {
    records = this.mapObjectsToStorable(records);
    await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
    const { statement, values } = this.databaseService.generateInsertStatement(TABLE_NAME, records);
    this.logger.debug('Executing INSERT STATEMENT', statement);
    return this.databaseService.query(statement, values);
  }

  public async truncate(): Promise<capSQLiteChanges> {
    return await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
  }

  public async retrieve(id: number): Promise<{ values: ListingPhoto[] }> {
    const results = await this.databaseService.select(TABLE_NAME, { id });
    return {
      values: this.mapResultsObject(results),
    };
  }

  public async retrieveAll(limit?: number): Promise<ListingPhoto[]> {
    const results = await this.databaseService.selectAll(TABLE_NAME, undefined, limit);
    return this.mapResultsObject(results);
  }

  public async retrieveForListingId(listingId: number): Promise<ListingPhoto[]> {
    const results = await this.databaseService.select(TABLE_NAME, { listing_id: listingId });
    return this.mapResultsObject(results);
  }

  public async retrieveBatch(listingId: number[]): Promise<ListingPhoto[]> {
    if (listingId.length > 0) {
      let statement = `SELECT * FROM ${TABLE_NAME} WHERE listing_id IN(`;
      const values: string[] = [];
      listingId.forEach((el) => {
        statement += '?, ';
        values.push(el.toString());
      });
      statement = statement.substr(0, statement.length - 2) + `);`;
      const results = await this.databaseService.query(statement, values);
      return this.mapResultsObject(results);
    } else {
      return [];
    }
  }

  public async remove(id: string): Promise<capSQLiteValues> {
    return await this.databaseService.delete(TABLE_NAME, { id });
  }

  public async removeByListingId(id: string): Promise<capSQLiteValues> {
    return await this.databaseService.delete(TABLE_NAME, { listing_id: id });
  }

  private mapResultsObject(results: capSQLiteValues): ListingPhoto[] {
    if (results.values) {
      return results.values?.map((record) => {
        return {
          id: record.id,
          listingId: record.listing_id,
          caption: record.caption,
          key: record.key,
        };
      });
    } else {
      return [];
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapObjectsToStorable(items: ListingPhoto[]): any[] {
    if (items) {
      return items?.map((record: ListingPhoto) => {
        return {
          id: record.id,
          listing_id: record.listingId,
          caption: record.caption,
          key: record.key,
        };
      });
    } else {
      return [];
    }
  }
}
